import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { plural } from './formato';
import { reafirmarInscricao } from './push';
import { Icone } from './Icone';
import { useTempoReal } from './tempoReal';
import './avisos.css';

/**
 * O sino da moldura — onda 548 (Plano 2 · Comercial, Etapa 1, Tarefa 4).
 *
 * Mostra quantos avisos a pessoa ainda não leu e leva à lista (`/avisos`).
 * Fica de pé sozinho, pelo tempo real: o aviso que nasce e o que é marcado
 * lido publicam em `usuario.<id>` (0503), e o sino pergunta a contagem de
 * novo (`GET /avisos?limite=0`). O `{ recarregar: true }` do tempo real (a
 * queda e a volta do fluxo, e a consulta a cada 30 s enquanto ele está fora)
 * é tratado do mesmo jeito: qualquer aviso no tópico é «pergunte de novo».
 *
 * Mora num arquivo à parte, e não na `Moldura.tsx`, para a moldura (que os
 * dois trilhos do Plano 2 mexem) ganhar uma linha só.
 */

/*
 * Dois eventos da própria página, para a tela de avisos e o sino não
 * abrirem dois fluxos do tempo real para o mesmo tópico:
 *  · `AVISOS_MUDARAM`: a tela marcou um lido, e o sino conta de novo sem
 *    esperar o servidor;
 *  · `AVISOS_CHEGARAM`: o sino recebeu o tempo real, e a tela (se estiver
 *    aberta) busca a lista de novo.
 */
export const AVISOS_MUDARAM = 'testesolo:avisos-mudaram';
export const AVISOS_CHEGARAM = 'testesolo:avisos-chegaram';

export function avisarQueOsAvisosMudaram(): void {
  window.dispatchEvent(new Event(AVISOS_MUDARAM));
}

export function SinoDeAvisos({ usuarioId }: { usuarioId: string }) {
  const [naoLidos, setNaoLidos] = useState(0);

  const contar = useCallback(async () => {
    try {
      const r = await api<{ naoLidos: number }>('/avisos?limite=0');
      setNaoLidos(r?.naoLidos ?? 0);
    } catch { /* sem rede: o sino fica com a última contagem */ }
  }, []);

  useEffect(() => {
    void contar();
    const aoMudar = () => { void contar(); };
    window.addEventListener(AVISOS_MUDARAM, aoMudar);
    return () => window.removeEventListener(AVISOS_MUDARAM, aoMudar);
  }, [contar]);

  /* Quem está na sessão assume a inscrição deste navegador (I-3), também no
     «Entrar como» e no «Voltar a ser eu», que trocam o `usuarioId`. */
  useEffect(() => { void reafirmarInscricao().catch(() => undefined); }, [usuarioId]);

  useTempoReal([`usuario.${usuarioId}`], () => {
    void contar();
    window.dispatchEvent(new Event(AVISOS_CHEGARAM));
  });

  const rotulo = naoLidos > 0 ? `Avisos, ${plural(naoLidos, 'não lido', 'não lidos')}` : 'Avisos';
  return (
    <Link to="/avisos" className="sino-de-avisos" aria-label={rotulo} title={rotulo}>
      <Icone nome="sino" />
      <span className="contagem" aria-hidden="true">{naoLidos > 0 ? (naoLidos > 99 ? '99+' : String(naoLidos)) : ''}</span>
    </Link>
  );
}
